/* External */
import {
  lazy, memo, Suspense, useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';

/* Components */
import { UPDATE_PROFILES } from '~/storage/profiles/actionTypes';
import Private from '../routes/Private';
import FullScreenMessage, { MessageId } from '../components/FullScreenMessage';

/* Storage */
import { cleanAllEntities, stopAuthenticatedSagas } from '../storage/utils/actions';

/* Services */
import { gaService } from '../services/gaService';

/* Utils */
import { Routes, getPath, getLink } from '../utils/router';
import { useLoading } from '../providers/LoadingProvider';
import { useOnBackPress } from '../utils/keyDownEvents';
import { noOp } from '../utils/functions';
import { useIsLoadingUserAccount, useIsLoggedIn, useIsUpdatingUserCustomData } from '../providers/UserProvider';
import useAnalitycNavigation from '../hooks/useAnalitycNavigation';
import { useUpdateConnectionStatus, useShowDisconnectedMessage } from '../providers/ConnectionStatusProvider';
import { useDeviceInfo } from '../providers/DeviceInfoProvider';
import platform from '../utils/platform';
import { isLowEndDevice } from '../utils/platform/isLowEndDevice';
import { previousUrlAvailable } from '../utils/history';
import { REPORT_GA } from '../utils/constants';
import { useIsFetchingConfig } from '../providers/ConfigProvider/ConfigProvider';
import { useIsForegroundApp } from '../providers/ForegroundAppProvider';
import { GtmLocalProvider } from '../providers/GtmLocalProvider';
import { MqttProvider } from '../providers/MqttProvider';
import { useGoogleTagsCommon } from '../utils/googleTags';
import { useTermsOfServiceInfo } from '../providers/TermsOfServiceProvider';

/* Styles */
import './App.scss';
import '../theme/colors';
import '../theme/fonts';

/* Route-level code splitting: these routes are heavy (auth flows, error/notice
   pages) and only needed once the app decides which top-level branch to render.
   Each lazy route renders its own loader through LoadingProvider, so a null
   fallback is safe here (a non-null fallback would double-spinner). */
const Logout = lazy(() => import('../routes/Logout'));
const ErrorPage = lazy(() => import('../routes/ErrorPage'));
const MessagePage = lazy(() => import('../routes/MessagePage'));
const Public = lazy(() => import('../routes/Public'));

function App({ hideLoading = noOp, showLoading = noOp }) {
  const [redirectTo, setRedirectTo] = useState(undefined);
  const { isLoading } = useLoading();
  const history = useHistory();
  const isLoggedIn = useIsLoggedIn();
  const isLoadingUser = useIsLoadingUserAccount();
  const isUpdatingUser = useIsUpdatingUserCustomData();
  const isFetchingConfig = useIsFetchingConfig();
  const isForegroundApp = useIsForegroundApp();
  const { isLoadingShouldAccept } = useTermsOfServiceInfo();
  const dispatch = useDispatch();
  useAnalitycNavigation();
  const showDisconnectedMessage = useShowDisconnectedMessage();
  const deviceInfo = useDeviceInfo() || {};
  const updateConnectionStatus = useUpdateConnectionStatus();

  /* Low-end devices (see isLowEndDevice — webOS 3/4 via both the OS-version
     rule and the UA floor): drop CSS transitions everywhere, and the looping
     decorative animations listed under .dtv-html5-s-low-end in App.scss. */
  const animationClassName = isLowEndDevice(deviceInfo)
    ? 'dtv-html5-s-no-transition-and-animation dtv-html5-s-low-end'
    : '';

  const getPathRedirect = useCallback((url) => {
    const match = /^directvgo:(?:\/\/[^/]+)?(\/.*)$/i.exec(url);
    if (match) {
      const [, path] = match;
      return path;
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: UPDATE_PROFILES });
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn && !isLoadingUser) {
      dispatch(cleanAllEntities());
      dispatch(stopAuthenticatedSagas());
    }
  }, [dispatch, isLoadingUser, isLoggedIn]);

  useEffect(() => {
    if (isLoading
        || isLoadingUser
        || isFetchingConfig
        || isUpdatingUser
        || isLoadingShouldAccept
    ) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [
    hideLoading,
    showLoading,
    isLoading,
    isLoadingUser,
    isFetchingConfig,
    isUpdatingUser,
    isLoadingShouldAccept,
  ]);

  useEffect(() => platform.addRelaunchEventListener((url) => {
    updateConnectionStatus();

    const path = getPathRedirect(url);
    if (path) {
      history.push(path);
    }
  }), [getPathRedirect, history, updateConnectionStatus]);

  useEffect(() => {
    if (isForegroundApp) {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_ACTION_OPEN_APP,
        label: REPORT_GA.GA_EVENT_LABEL_OPEN_APP,
        category: REPORT_GA.GA_EVENT_CATEGORY_OPEN_APP,
      });
    }
  }, [isForegroundApp]);

  const onBackPress = useCallback(() => {
    if (previousUrlAvailable()) {
      history.goBack();
    } else {
      history.push(getLink(Routes.Private.Home));
    }
  }, [history]);

  useEffect(() => {
    async function setRedirectToFromPlatform() {
      const url = await platform.getUrlToOpen();
      const path = getPathRedirect(url);
      if (path) {
        setRedirectTo(path);
      } else {
        setRedirectTo('');
      }
    }

    setRedirectToFromPlatform();
  }, [getPathRedirect]);

  useOnBackPress({ listener: onBackPress, priority: -1 });

  return (
    <GtmLocalProvider commonDimensionsData={useGoogleTagsCommon()}>
      <MqttProvider>
        <div className={animationClassName}>
          {/* Lazy routes render their own loaders through LoadingProvider; a
              non-null fallback would double-spinner. */}
          {/* eslint-disable-next-line unicorn/no-null
              -- non-null fallback would double-spinner with LoadingProvider */}
          <Suspense fallback={null}>
            <Switch>
              <Route path={getPath(Routes.Logout)} component={Logout} />
              <Route path={getPath(Routes.ErrorPage)} component={ErrorPage} />
              <Route path={getPath(Routes.MessagePage)} component={MessagePage} />
              <Route path={getPath(Routes.Public)} component={Public} />
              <Route path={getPath(Routes.Private)} component={Private} />
              {redirectTo !== undefined && (
              <Switch>
                {!!redirectTo && <Redirect to={redirectTo} />}
                {!isLoadingUser
                    && (isLoggedIn ? (
                      <Redirect to={getLink(Routes.Private)} />
                    ) : (
                      <Redirect to={getLink(Routes.Public)} />
                    ))}
              </Switch>
              )}
            </Switch>
          </Suspense>
          {showDisconnectedMessage && (
          <FullScreenMessage messageKey={MessageId.LOST_CONNECTION} />
          )}
        </div>
      </MqttProvider>
    </GtmLocalProvider>
  );
}

App.propTypes = {
  hideLoading: PropTypes.func,
  showLoading: PropTypes.func,
};

export default memo(App);
